import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import FilterToolbar from '../../components/FilterToolbar';
import SectionCard from '../../components/SectionCard';
import InvDataGrid from '../../components/InvDataGrid';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import StoreMasterShell from '../../components/store/StoreMasterShell';
// import StoreTypeChip from '../../components/store/StoreTypeChip';
import StoreFlagBadges from '../../components/store/StoreFlagBadges';
import { getStores, deleteStore } from '../../api/stores';
import { /* STORE_TYPES, */ STORE_PAGE_SIZE } from '../../constants/storeManagement';
import { STATUS_FILTER_OPTIONS } from '../../constants';
import {
  normalizeStoreList, storeRowId, toStoreListPage,
  // storeTypeLabel,
} from '../../utils/storeEntity';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';
import useMasterList from '../../hooks/useMasterList';

function buildListParams({ page, search, statusFilter, typeFilter, pageSize }) {
  const params = { limit: pageSize, offset: page * pageSize };
  if (search.trim()) params.q = search.trim();
  if (typeFilter) params.store_type = typeFilter;
  if (statusFilter === 'active') params.is_active = true;
  if (statusFilter === 'inactive') params.is_active = false;
  return params;
}

export default function StoreList() {
  const history = useHistory();
  const { can } = useInvRole();
  const { url } = useRouteMatch();
  const base = url.replace(/\/stores\/?$/, '');

  const list = useMasterList({ pageSize: STORE_PAGE_SIZE });
  const {
    items, setItems, totalCount, setTotalCount,
    loading, setLoading, error, setError,
    page, setPage, searchInput, setSearchInput,
    search, statusFilter, setStatusFilter,
    resetFilters, pageSize,
  } = list;

  const [typeFilter, setTypeFilter] = useState('');
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const hasActiveFilters = Boolean(search.trim() || statusFilter || typeFilter);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await getStores(buildListParams({
        page, search, statusFilter, typeFilter, pageSize,
      }));
      const { items: fetched, total } = toStoreListPage(raw, { page, pageSize });
      setItems(fetched.map(normalizeStoreList));
      setTotalCount(total);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, typeFilter, pageSize, setLoading, setError, setItems, setTotalCount]);

  useEffect(() => { load(); }, [load]);

  function goDetail(id) {
    history.push(`${base}/stores/${id}`);
  }

  function goCreate() {
    history.push(`${base}/stores/new`);
  }

  async function confirmDeactivate() {
    if (!can(INV_ACTIONS.STORE_DEACTIVATE)) return;
    setDeactivating(true);
    try {
      await deleteStore(storeRowId(deactivateTarget));
      setDeactivateTarget(null);
      toast.success('Store deactivated successfully');
      await load();
    } catch (e) {
      if (e.code === 'STORE_HAS_ACTIVE_CHILDREN') {
        toast.error('Cannot deactivate — deactivate all sub-stores first. Open the store to manage sub-stores from the Hierarchy tab.');
      } else {
        toast.error(e.message || 'Failed to deactivate store');
      }
      setDeactivateTarget(null);
    } finally {
      setDeactivating(false);
    }
  }

  // const typeOptions = useMemo(() => [
  //   { value: '', label: 'All Types' },
  //   ...STORE_TYPES.map(t => ({ value: t, label: storeTypeLabel(t) })),
  // ], []);

  const columns = useMemo(() => [
    { field: 'store_code', headerName: 'Code', width: 100 },
    { field: 'store_name', headerName: 'Store Name', flex: 1, minWidth: 160 },
    // {
    //   field: 'store_type', headerName: 'Type', width: 130,
    //   renderCell: p => <StoreTypeChip type={p.value} />,
    // },
    {
      field: 'flags', headerName: 'Capabilities', width: 140,
      sortable: false,
      renderCell: p => <StoreFlagBadges store={p.row} />,
    },
    {
      field: 'bin_count', headerName: 'Bins', width: 70, align: 'center', headerAlign: 'center',
    },
    {
      field: 'is_active', headerName: 'Active', width: 80, align: 'center', headerAlign: 'center',
      renderCell: p => p.value !== false
        ? <CheckCircleOutlineIcon sx={{ color: IPD_COLORS.success.text, fontSize: 18 }} />
        : <CancelIcon sx={{ color: IPD_COLORS.error.text, fontSize: 18 }} />,
    },
    {
      field: 'actions', headerName: '', width: 80, sortable: false,
      renderCell: p => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View details">
            <IconButton size="small" onClick={() => goDetail(storeRowId(p.row))} sx={{ color: IPD_COLORS.primary }}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.row.is_active !== false && can(INV_ACTIONS.STORE_DEACTIVATE) && (
            <Tooltip title="Deactivate store">
              <IconButton size="small" onClick={() => setDeactivateTarget(p.row)} sx={{ color: IPD_COLORS.error.text }}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [base, history, can]);

  return (
    <StoreMasterShell
      title="Store Master"
      description="Configure storage locations — receiving, issue, pharmacy, bins, and transaction permissions."
      badge={`${totalCount} ${totalCount === 1 ? 'store' : 'stores'}`}
      actions={can(INV_ACTIONS.STORE_CREATE) ? (
        <InvButton startIcon={<AddIcon />} onClick={goCreate}>
          New Store
        </InvButton>
      ) : null}
    >
      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="Search by name or code…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          inputProps={{ 'aria-label': 'Search stores' }}
        />
        {/* <FilterToolbar.Filter
          label="Store Type"
          width="wide"
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(0); }}
          options={typeOptions}
        /> */}
        <FilterToolbar.Filter
          label="Status"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
          options={STATUS_FILTER_OPTIONS}
        />
        {hasActiveFilters && (
          <FilterToolbar.Reset
            label="Clear"
            onClick={() => {
              resetFilters();
              setTypeFilter('');
            }}
          />
        )}
      </FilterToolbar>

      <SectionCard title="Stores" noPadding sx={{ mt: 2.5 }}>
        <InvDataGrid
          embedded
          fillHeight
          rows={items}
          columns={columns}
          loading={loading}
          error={error}
          onRetry={load}
          getRowId={r => storeRowId(r)}
          emptyMessage="No stores yet."
          emptyDescription="Create your first store to enable GRN, transfers, and dispensing workflows."
          emptyAction={can(INV_ACTIONS.STORE_CREATE)
            ? <InvButton size="small" startIcon={<AddIcon />} onClick={goCreate}>New Store</InvButton>
            : null}
          onRowClick={p => goDetail(storeRowId(p.row))}
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={m => setPage(m.page)}
          pageSizeOptions={[10, 25, 50]}
          sx={{ cursor: 'pointer' }}
        />
      </SectionCard>

      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate Store"
        description={`Deactivate "${deactivateTarget?.store_name}"? Deactivate sub-stores first if any exist.`}
        destructive
        loading={deactivating}
        onConfirm={confirmDeactivate}
        onCancel={() => setDeactivateTarget(null)}
      />
    </StoreMasterShell>
  );
}
